/** @format */
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    EventEmitter,
    Inject,
    input,
    Optional,
    Output,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { Attainment, CourseUnit, OtmId, SubstituteFor } from 'common-typescript/types';
import { PLAN_ACTIONS_SERVICE_INJECTION_TOKEN, PlanActionsService } from '../../plan/plan-actions-service/plan-actions.service';
import { RemovedSubstitution } from '../../plan/plan-manager/plan-manager.service';
import { Action, InputType } from '../../study-boxes/study-action-box/study-action-box.component';
import { SubstituteCourseUnit } from '../../study-boxes/study-substitution-box/study-substitution-box.component';
import { PlanStructureContext } from '../plan-structure-context';

@Component({
    selector: 'sis-plan-structure-course-unit-substitutions',
    templateUrl: './plan-structure-course-unit-substitutions.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class PlanStructureCourseUnitSubstitutionsComponent {
    readonly substitutedCourseUnit = input.required<CourseUnit>();
    readonly headingLevel = input.required<number>();
    readonly showCompletionMethodSelection = input<boolean>(true);
    readonly showAttainment = input<boolean>(true);
    readonly removedSubstitution = input<RemovedSubstitution>();
    readonly action = input<Action>();
    readonly selected = input<boolean>();
    readonly inputType = input<InputType>();
    readonly actionInputId = input<string>();
    readonly actionInputName = input<string>();
    @Output() actionChange = new EventEmitter<void>();

    protected readonly substituteCourseUnits = computed<SubstituteCourseUnit[]>(() => {
        const { courseUnits } = this.planStructureContext.planStateObject();
        const { courseUnitsById, attainmentsById } = this.planStructureContext.planData();
        const substitutedCourseUnitId = this.substitutedCourseUnit().id;
        const removedSubstitution = this.removedSubstitution();
        if (!courseUnits[substitutedCourseUnitId]?.isSubstituted && removedSubstitution) {
            return this.getRemovedSubstituteCourseUnits(removedSubstitution, substitutedCourseUnitId);
        }
        return courseUnits[substitutedCourseUnitId].substitutedBy.map(substituteId => {
            const courseUnitStateObject = courseUnits[substituteId];
            return {
                ...this.toSubstituteCourseUnit(
                    courseUnitsById[substituteId],
                    attainmentsById[courseUnitStateObject.attainmentId],
                    courseUnitStateObject.substituteFor,
                    substitutedCourseUnitId,
                ),
                isAttainmentAboutToExpire: courseUnitStateObject.isAttainmentAboutToExpire,
                attainmentExpiryDate: courseUnitStateObject.attainmentExpiryDate,
                courseUnitSelection: courseUnitStateObject.courseUnitSelection,
            };
        });
    });

    protected readonly planStructureContext = inject(PlanStructureContext);

    constructor(@Optional() @Inject(PLAN_ACTIONS_SERVICE_INJECTION_TOKEN) private planActionsService: PlanActionsService) {}

    openCourseUnitInfoModal(courseUnitId: OtmId): void {
        const courseUnit =
            this.planStructureContext.planData().courseUnitsById[courseUnitId] ??
            this.removedSubstitution()?.substituteCourseUnits.find(cu => cu.id === courseUnitId);
        this.planActionsService?.openCourseUnitInfoModal(courseUnit);
    }

    private getRemovedSubstituteCourseUnits(
        removedSubstitution: RemovedSubstitution,
        substitutedCourseUnitId: OtmId,
    ): SubstituteCourseUnit[] {
        return removedSubstitution.substituteCourseUnits.map(substituteCourseUnit =>
            this.toSubstituteCourseUnit(
                substituteCourseUnit,
                removedSubstitution.substituteAttainmentsByCourseUnitId[substituteCourseUnit.id],
                removedSubstitution.substituteCourseUnitSelections.find(cus => cus.courseUnitId === substituteCourseUnit.id)?.substituteFor,
                substitutedCourseUnitId,
            ),
        );
    }

    private toSubstituteCourseUnit(
        substituteCourseUnit: CourseUnit,
        attainment: Attainment | undefined,
        substituteFor: SubstituteFor[] | undefined,
        substitutedCourseUnitId: OtmId,
    ): SubstituteCourseUnit {
        const substituteCredits = substituteFor?.find(sf => sf.substitutedCourseUnitId === substitutedCourseUnitId)?.substitutedCredits;
        return {
            id: substituteCourseUnit.id,
            name: substituteCourseUnit.name,
            code: substituteCourseUnit.code,
            // If the substitute course unit has no credits all credits are assumed to be used
            isPartialSubstitute: !!substituteCredits,
            // In order: marked substitute credits -> attainment credits -> substitute course unit credits
            substituteCredits: substituteCredits ?? attainment?.credits ?? substituteCourseUnit.credits,
            credits: substituteCourseUnit.credits,
            gradeId: attainment?.gradeId,
            gradeScaleId: attainment?.gradeScaleId,
            attainedCredits: attainment?.credits,
        };
    }

    openSelectSubstitutionModal(): void {
        this.planActionsService?.openSelectSubstitutionModal?.(this.substitutedCourseUnit());
    }
}
